import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import PluginService from "api/pluginService";
import {
  EScanColorMode,
  EScanJobState,
  EScanSource,
  IScannerDevice,
} from "dto/Plugin/scan";
import { IScanPageVM } from "./typings";

export interface IScanSettings {
  deviceId: string;
  dpi: number;
  colorMode: EScanColorMode;
  source: EScanSource;
  duplex: boolean;
}

export const DPI_OPTIONS = [150, 200, 300, 600];

const STORAGE_KEY = "nota.scan.settings";

const DEFAULT_SETTINGS: IScanSettings = {
  deviceId: "",
  dpi: 300,
  colorMode: EScanColorMode.Color,
  source: EScanSource.Flatbed,
  duplex: false,
};

// Wczytanie ostatnich ustawień skanowania z localStorage (z walidacją pól).
const loadSettings = (): IScanSettings => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const s = JSON.parse(raw);
    return {
      deviceId:
        typeof s.deviceId === "string" ? s.deviceId : DEFAULT_SETTINGS.deviceId,
      dpi: typeof s.dpi === "number" ? s.dpi : DEFAULT_SETTINGS.dpi,
      colorMode: Object.values(EScanColorMode).includes(s.colorMode)
        ? s.colorMode
        : DEFAULT_SETTINGS.colorMode,
      source: Object.values(EScanSource).includes(s.source)
        ? s.source
        : DEFAULT_SETTINGS.source,
      duplex: typeof s.duplex === "boolean" ? s.duplex : DEFAULT_SETTINGS.duplex,
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
};

// Wypala obrót do bitmapy i zwraca JPEG + wymiary po obrocie.
const blobToRotatedJpeg = (
  blob: Blob,
  rotation: number,
): Promise<{ dataUrl: string; w: number; h: number }> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const swap = rotation === 90 || rotation === 270;
      const w = swap ? img.height : img.width;
      const h = swap ? img.width : img.height;
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error("no_2d_context"));
        return;
      }
      ctx.translate(w / 2, h / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      URL.revokeObjectURL(url);
      resolve({ dataUrl: canvas.toDataURL("image/jpeg", 0.92), w, h });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("image_decode_failed"));
    };
    img.src = url;
  });
};

export const useScanner = (open: boolean) => {
  const devicesQuery = PluginService.useGetScanners(open);
  const startScanM = PluginService.useStartScan();
  const cancelScanM = PluginService.useCancelScan();
  const deleteJobM = PluginService.useDeleteScanJob();

  const [settings, setSettings] = useState<IScanSettings>(loadSettings);

  const [activeJobId, setActiveJobId] = useState<string>();
  const [scanning, setScanning] = useState(false);
  const [pages, setPages] = useState<IScanPageVM[]>([]);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState<string>();

  const jobsRef = useRef<string[]>([]); // wszystkie zadania do posprzątania
  const jobDpiRef = useRef<Record<string, number>>({});
  const seenRef = useRef<Set<string>>(new Set()); // klucze już dodanych stron

  const devices = useMemo(() => devicesQuery.data ?? [], [devicesQuery.data]);

  // Zapisuj ostatnie ustawienia do localStorage.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      /* ignoruj (np. tryb prywatny) */
    }
  }, [settings]);

  // Waliduj skaner/źródło/dupleks względem realnie dostępnych urządzeń:
  // przywrócony skaner może już nie istnieć, a źródło może nie być wspierane.
  useEffect(() => {
    if (!devices.length) return;
    setSettings((s) => {
      let next = s;
      if (!devices.some((d) => d.Id === s.deviceId)) {
        next = { ...next, deviceId: devices[0].Id };
      }
      const dev = devices.find((d) => d.Id === next.deviceId);
      if (dev && next.source === EScanSource.Feeder && !dev.HasFeeder) {
        next = { ...next, source: EScanSource.Flatbed };
      }
      if (dev && next.duplex && !dev.HasDuplex) {
        next = { ...next, duplex: false };
      }
      return next === s ? s : next;
    });
  }, [devices, settings.deviceId]);

  const jobQuery = PluginService.useScanJob(activeJobId, scanning);

  // scalanie stron dochodzących z aktywnego zadania (poll)
  useEffect(() => {
    const data = jobQuery.data;
    if (!data || data.JobId !== activeJobId) return;

    setPages((prev) => {
      const additions: IScanPageVM[] = [];
      for (const p of data.Pages) {
        const key = `${data.JobId}:${p.Index}`;
        if (seenRef.current.has(key)) continue;
        seenRef.current.add(key);
        additions.push({
          key,
          jobId: data.JobId,
          index: p.Index,
          dpi: jobDpiRef.current[data.JobId] ?? settings.dpi,
          thumbnailUrl: `data:image/jpeg;base64,${p.Thumbnail}`,
          rotation: 0,
          selected: true,
          width: p.Width,
          height: p.Height,
        });
      }
      return additions.length ? [...prev, ...additions] : prev;
    });

    if (data.State !== EScanJobState.Scanning) {
      setScanning(false);
      if (data.State === EScanJobState.Faulted) {
        setError(data.Error || "scan_failed");
      }
    }
  }, [jobQuery.data, activeJobId, settings.dpi]);

  const selectedDevice: IScannerDevice | undefined = devices.find(
    (d) => d.Id === settings.deviceId,
  );

  const startScan = useCallback(async () => {
    if (!settings.deviceId || scanning) return;
    setError(undefined);
    try {
      const res = await startScanM.mutateAsync({
        DeviceId: settings.deviceId,
        Dpi: settings.dpi,
        ColorMode: settings.colorMode,
        Source: settings.source,
        Duplex: settings.source === EScanSource.Feeder && settings.duplex,
      });
      jobsRef.current.push(res.JobId);
      jobDpiRef.current[res.JobId] = settings.dpi;
      setActiveJobId(res.JobId);
      setScanning(true);
    } catch {
      setError("scan_failed");
    }
  }, [settings, scanning, startScanM]);

  const stopScan = useCallback(async () => {
    if (activeJobId) {
      try {
        await cancelScanM.mutateAsync(activeJobId);
      } catch {
        /* ignoruj — i tak zatrzymujemy poll */
      }
    }
    setScanning(false);
  }, [activeJobId, cancelScanM]);

  const toggleSelect = useCallback((key: string) => {
    setPages((prev) =>
      prev.map((p) => (p.key === key ? { ...p, selected: !p.selected } : p)),
    );
  }, []);

  const rotatePage = useCallback((key: string) => {
    setPages((prev) =>
      prev.map((p) =>
        p.key === key ? { ...p, rotation: (p.rotation + 90) % 360 } : p,
      ),
    );
  }, []);

  const removePage = useCallback((key: string) => {
    setPages((prev) => prev.filter((p) => p.key !== key));
  }, []);

  const reorder = useCallback((from: number, to: number) => {
    setPages((prev) => {
      if (from === to || to < 0 || to >= prev.length || from < 0 || from >= prev.length) {
        return prev;
      }
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  const setAllSelected = useCallback((val: boolean) => {
    setPages((prev) => prev.map((p) => ({ ...p, selected: val })));
  }, []);

  const cleanup = useCallback(async () => {
    const ids = [...jobsRef.current];
    jobsRef.current = [];
    jobDpiRef.current = {};
    seenRef.current = new Set();
    await Promise.all(
      ids.map((id) => deleteJobM.mutateAsync(id).catch(() => undefined)),
    );
  }, [deleteJobM]);

  // sprzątnij zadania (temp na wtyczce) przy odmontowaniu modalu
  const cleanupRef = useRef(cleanup);
  cleanupRef.current = cleanup;
  useEffect(() => {
    return () => {
      void cleanupRef.current();
    };
  }, []);

  const buildPdf = useCallback(
    async (fileName: string): Promise<File | null> => {
      const chosen = pages.filter((p) => p.selected);
      if (!chosen.length) return null;
      setBuilding(true);
      try {
        let doc: jsPDF | null = null;
        for (const page of chosen) {
          const blob = await PluginService.getScanPage(page.jobId, page.index);
          const { dataUrl, w, h } = await blobToRotatedJpeg(blob, page.rotation);
          const dpi = page.dpi || 300;
          const ptW = (w / dpi) * 72;
          const ptH = (h / dpi) * 72;
          if (!doc) {
            doc = new jsPDF({ unit: "pt", format: [ptW, ptH] });
          } else {
            doc.addPage([ptW, ptH]);
          }
          doc.addImage(dataUrl, "JPEG", 0, 0, ptW, ptH);
        }
        const pdfBlob = doc!.output("blob");
        return new File([pdfBlob], `${fileName}.pdf`, {
          type: "application/pdf",
        });
      } catch {
        setError("pdf_failed");
        return null;
      } finally {
        setBuilding(false);
      }
    },
    [pages],
  );

  // Pełny JPEG strony (z wypalonym obrotem) jako data URL — do podglądu/powiększenia.
  const loadPagePreview = useCallback(
    async (page: IScanPageVM): Promise<string> => {
      const blob = await PluginService.getScanPage(page.jobId, page.index);
      const { dataUrl } = await blobToRotatedJpeg(blob, page.rotation);
      return dataUrl;
    },
    [],
  );

  return {
    devices,
    devicesLoading: devicesQuery.isLoading,
    pluginMissing: devicesQuery.isError,
    selectedDevice,
    settings,
    setSettings,
    scanning,
    scanStarting: startScanM.isLoading,
    pages,
    building,
    error,
    startScan,
    stopScan,
    toggleSelect,
    rotatePage,
    removePage,
    reorder,
    setAllSelected,
    buildPdf,
    loadPagePreview,
    cleanup,
  };
};
